import { type FC, useMemo } from 'react'

import { useExperimentsPageType } from '../../experiments/components/ExperimentsPageTypeContext'
import { ExperimentPageType } from '../../experiments/ExperimentPageType'
import { useFullPath } from '../../generic-hooks'
import { useGlobalConfigContext } from '../../global-config'
import { TENANT } from '../../tenant'
import { type HeaderDataType } from '../header/model/headerTypes'

import { StickyBannerBody } from './StickyBannerBody'
import { useStickyBannerContext } from './StickyBannerContext'

const SHOW_ON_PDP_TENANTS = new Set([TENANT.CH, TENANT.COM])

// eslint-disable-next-line max-statements -- the rule was disabled before a comment became required
export const StickyBanner: FC<{ bannerData: HeaderDataType }> = ({
  bannerData,
}) => {
  const { tenant } = useGlobalConfigContext()

  const blacklistPages: ExperimentPageType[] = useMemo(() => {
    return [
      ExperimentPageType.Account,
      ExperimentPageType.Cart,
      ExperimentPageType.CartIntermediate,
      ExperimentPageType.Login,
      // conditionally exclude PDP from blacklist for some tenants
      ...(SHOW_ON_PDP_TENANTS.has(tenant) ? [] : [ExperimentPageType.Product]),
    ]
  }, [tenant])

  const pageType = useExperimentsPageType()
  const fullPath = useFullPath()
  const stickyBannerContext = useStickyBannerContext()

  /**
   * in case other component wants to hide the sticky banner, it will set it via context.
   * this have precedence over other visibility conditions
   */
  if (stickyBannerContext?.forceHidden) {
    return null
  }

  if (blacklistPages.includes(pageType)) {
    return null
  }

  const banner = bannerData.cmsMenu.items?.find((item) => {
    if (item.schema === 'cms.component.StickyBanner') {
      return (
        item.endDate &&
        item.startDate &&
        new Date(item.endDate).getTime() - Date.now() > 0 &&
        new Date(item.startDate) < new Date()
      )
    }

    return false
  })

  if (banner?.schema !== 'cms.component.StickyBanner') {
    return null
  }

  // if whitelist field is pressent then the banner is displayed only on the paths that match the whitelist
  const whiteListedUrls = banner.whiteList?.split(',')
  if (
    fullPath &&
    whiteListedUrls &&
    !whiteListedUrls.some((it) => fullPath.includes(it))
  ) {
    return null
  }

  // if blacklist field is pressent then the banner is not displayed if the current path matches the blacklist
  const blackListedUrls = banner.blackList?.split(',')
  if (fullPath && blackListedUrls?.some((it) => fullPath.includes(it))) {
    return null
  }

  return <StickyBannerBody banner={banner} />
}
